import { Context } from '@blackbird/ui-base';
import { ISnackbarContext } from '@blackbird/ui-base/contexts/snackbar';
import * as CloudFrontLayer from '@blackbird/ui-base/helpers/cloudfront-layer';
import { Grid } from '@material-ui/core';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import { CognitoIdentityServiceProvider as Cognito } from 'aws-sdk';
import fetch from 'isomorphic-fetch';
import { GetServerSideProps } from 'next';
import { WithRouterProps } from 'next/dist/client/with-router';
import { withRouter } from 'next/router';
import React from 'react';

import DefaultLogin from '@/components/auth/default-login';
import ForgotPassword from '@/components/auth/forgot-password';
import NewPassword from '@/components/auth/new-password';
import { handler } from '@/lambda-edge/origin-request';

const protect = CloudFrontLayer.proxy(handler);

// import MultiFactor from 'components/auth/multi-factor';

const styles = (theme: Theme) =>
  createStyles({
    fillHeight: {
      height: '100%',
    },
    gridItem: {
      [theme.breakpoints.down('xs')]: {
        height: '100%',
        width: '100%',
      },
    },
    formControl: {
      margin: theme.spacing(),
    },
  });

interface Properties {
  env?: any;
}

interface State {
  type: string;
  username: string;
  password: string;
  challengeName: string | null;
  requiredAttributes: any;
  passwordPolicy: Cognito.PasswordPolicyType;
  session: string | null;
  isLoading: boolean;
  isSuccessful: boolean;
  formError: boolean;
}

class Login extends React.Component<Properties & WithRouterProps & WithStyles<typeof styles>, State> {
  readonly state: State = {
    type: 'login',
    username: '',
    password: '',
    requiredAttributes: [],
    challengeName: null,
    session: null,
    passwordPolicy: {},
    isLoading: false,
    isSuccessful: false,
    formError: false,
  };

  private onSubmit = (snackbar: ISnackbarContext) => async (e: any, payload: Record<string, string>) => {
    const {
      router: { query },
    } = this.props;

    e.preventDefault();

    const [clientId] = Array.isArray(query.clientId) ? query.clientId : [query.clientId];
    payload.clientId = clientId ?? '';
    const [authorizationCode] = Array.isArray(query.authorizationCode)
      ? query.authorizationCode
      : [query.authorizationCode];
    payload.authorizationCode = authorizationCode ?? '';

    this.setState({ isLoading: true });

    const encodedForm = Object.keys(payload)
      .map((key) => {
        return encodeURIComponent(key) + '=' + encodeURIComponent(payload[key]);
      })
      .join('&');

    const response = await fetch(`${window.location.origin}${window.location.pathname}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: encodedForm,
      credentials: 'same-origin',
    });
    let info: any;

    switch (response.status) {
      case 200:
        this.setState({ isLoading: false, isSuccessful: true });
        const loginResponse = await response.json();
        setTimeout(
          () =>
            window.location.assign(
              `${loginResponse.authCallback}?authorizationCode=${loginResponse.authorizationCode}`,
            ),
          1000,
        );
        return true;

      case 400:
      case 401:
      case 406:
        info = await response.json();
        if (!info.message) {
          info.message = 'Something unexpected happened.';
        }

        snackbar.spawn(info.message, 'error');

        return this.setState({ isLoading: false, formError: true });

      case 403:
        info = await response.json();
        if (!info.message) {
          info.message = 'Access to an external resource was forbidden!';
        }

        snackbar.spawn(info.message, 'error');

        return this.setState({ isLoading: false, formError: true });

      case 412:
        info = await response.json();
        if (!info.requiredAttributes) {
          info.requiredAttributes = [];
        }
        if (!info.passwordPolicy) {
          info.passwordPolicy = {};
        }
        return this.setState({
          isLoading: false,
          type: info.type === 'NEW' ? 'tempPassword' : 'resetPassword',
          formError: false,
          requiredAttributes: info.requiredAttributes,
          challengeName: info.challengeName,
          passwordPolicy: info.passwordPolicy,
          session: info.session,
        });

      case 419:
        info = await response.json();
        if (!info.message) {
          info.message = 'Authentication session has expired.';
        }

        snackbar.spawn(`${info.message} Redirecting to ${info.redirect}...`, 'error');

        return setTimeout(() => window.location.assign(`http://${info.redirect}`), 2500);

      case 424:
        return this.setState({
          isLoading: false,
          type: 'mfaCode',
          formError: false,
        });

      default:
        console.error(`Got ${response.status} response code`, response.body);

        snackbar.spawn('Something went wrong, check your internet connection and try again', 'error');

        return this.setState({
          isLoading: false,
          formError: true,
        });
    }
  };

  render() {
    const { classes } = this.props;

    const form = (snackbar: ISnackbarContext) => {
      switch (this.state.type) {
        case 'tempPassword':
          return (
            <NewPassword
              onChangeUser={(v: any) => this.setState({ username: v })}
              onChangePass={(v: any) => this.setState({ password: v })}
              username={this.state.username}
              password={this.state.password}
              challengeName={this.state.challengeName}
              requiredAttributes={this.state.requiredAttributes}
              passwordPolicy={this.state.passwordPolicy}
              session={this.state.session}
              isLoading={this.state.isLoading}
              onSubmit={this.onSubmit(snackbar)}
              isSuccessful={this.state.isSuccessful}
              type={'NEW'}
            />
          );
        case 'resetPassword':
          return (
            <NewPassword
              onChangeUser={(v: any) => this.setState({ username: v })}
              onChangePass={(v: any) => this.setState({ password: v })}
              username={this.state.username}
              password={this.state.password}
              challengeName={this.state.challengeName}
              passwordPolicy={this.state.passwordPolicy}
              isLoading={this.state.isLoading}
              onSubmit={this.onSubmit(snackbar)}
              isSuccessful={this.state.isSuccessful}
              type={'RESET'}
            />
          );
        case 'mfaCode':
        /*form = <NewPassword
             onSubmit={this.onSubmit}
             />;*/
        case 'forgotPassword':
          return (
            <ForgotPassword
              onChangeUser={(v: any) => this.setState({ username: v })}
              onChangePass={(v: any) => this.setState({ password: v })}
              username={this.state.username}
              onSubmit={this.onSubmit(snackbar)}
              isLoading={this.state.isLoading}
              isSuccessful={this.state.isSuccessful}
              onCancel={() => this.setState({ type: 'login' })}
            />
          );
        default:
          return (
            <DefaultLogin
              onChangeUser={(v: any) => this.setState({ username: v })}
              onChangePass={(v: any) => this.setState({ password: v })}
              username={this.state.username}
              password={this.state.password}
              onSubmit={this.onSubmit(snackbar)}
              isLoading={this.state.isLoading}
              isSuccessful={this.state.isSuccessful}
              onResetPassword={() => this.setState({ type: 'forgotPassword' })}
            />
          );
      }
    };

    return (
      <Context.Snackbar.Consumer>
        {(snackbar) => (
          <Grid item xs={12} className={classes.fillHeight}>
            <Grid container alignItems="center" justify="center" className={classes.fillHeight}>
              <Grid item className={classes.gridItem}>
                {form(snackbar)}
              </Grid>
            </Grid>
          </Grid>
        )}
      </Context.Snackbar.Consumer>
    );
  }
}

export const getServerSideProps =
  process.env.NODE_ENV === 'development'
    ? ((async ({ req, res }) => {
        await protect(req, res);

        return {
          props: {},
        };
      }) as GetServerSideProps)
    : undefined;

export default withRouter(withStyles(styles)(Login));
